import {ResourceAllocationState, Allocation} from '../../../types';

export const allocationHandlers = {
    AllocationAdded: (
        state: ResourceAllocationState,
        data: Allocation
    ): ResourceAllocationState => {
        return {
            ...state,
            allocations: {...state.allocations, [data.id]: data}
        };
    },
    AllocationUpdated: (
        state: ResourceAllocationState,
        data: {id: string; updates: Partial<Allocation>}
    ): ResourceAllocationState => {
        return {
            ...state,
            allocations: {
                ...state.allocations,
                [data.id]: {
                    ...state.allocations[data.id],
                    ...data.updates
                }
            }
        };
    },
    AllocationDeleted: (
        state: ResourceAllocationState,
        data: {id: string}
    ): ResourceAllocationState => {
        const {[data.id]: _removed, ...rest} = state.allocations;
        return {...state, allocations: rest};
    },
    // A whole multi-cell edit from another client, applied in one reducer pass
    // (one recompute) instead of one event per cell.
    AllocationsBulkUpdated: (
        state: ResourceAllocationState,
        data: {allocations: Allocation[]; deletedIds: string[]}
    ): ResourceAllocationState => {
        const allocations = {...state.allocations};
        for (const alloc of data.allocations) {
            allocations[alloc.id] = allocations[alloc.id]
                ? {...allocations[alloc.id], ...alloc}
                : alloc;
        }
        for (const id of data.deletedIds) {
            delete allocations[id];
        }
        return {...state, allocations};
    },
    // "Has booked hours anywhere" for the assignments a write touched. This is
    // a whole-timeline fact and `allocations` above is only the loaded window,
    // so the server owns it and says when it changes — see
    // BroadcastAllocatedAssignmentsAsync. Unlike the events above, this one
    // reaches the editing client too, which is equally unable to derive it.
    AllocatedAssignmentsChanged: (
        state: ResourceAllocationState,
        data: {nowAllocated?: string[]; nowUnallocated?: string[]}
    ): ResourceAllocationState => {
        const next = new Set(state.allocatedAssignmentIds);
        for (const id of data.nowAllocated ?? []) next.add(id);
        for (const id of data.nowUnallocated ?? []) next.delete(id);

        // Keep the array identity when nothing actually moved: this slice feeds
        // memoised grid/insight recomputes.
        const unchanged =
            next.size === state.allocatedAssignmentIds.length &&
            state.allocatedAssignmentIds.every(id => next.has(id));
        if (unchanged) return state;

        return {...state, allocatedAssignmentIds: [...next]};
    }
};
